'use client';

import {
	ReportBuilderConfigCard,
	type ReportBuilderConfigCardData,
	ReportBuilderConfigCardSkeleton,
	ReportBuilderView,
	type ReportSpec,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useGeneratedReports } from '@/hooks/use-generated-reports';
import { useMembers } from '@/hooks/use-members';
import { usePlants } from '@/hooks/use-plants';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { type ConfigToggleProps, useConfigToggle } from '@/hooks/use-config-toggle';
import { downloadReportBlob, reportPdfObjectUrl } from '@/lib/report-export';
import { loadReportLogo } from '@/lib/report-logo';
import { api } from '@/lib/utils/api';

const PRODUCER_ROLES = new Set(['producer', 'prosumer']);

/**
 * Data wiring for the Report Builder page. Presentation lives in @slims/ui
 * (ReportBuilderView). Come le altre pagine, con più configurazioni in ambito
 * compare il toggle «Panoramica»/«Configurazione singola»: la Panoramica è una
 * card per configurazione con la composizione soci, il clic apre il generatore
 * di quella configurazione. Report generation is a client-side demo.
 */
export default function ReportsPage() {
	const { scopes, loading: scopesLoading, effectiveLeaf, openConfig, toggle, showOverview } =
		useConfigToggle('reports');

	if (!effectiveLeaf) {
		return (
			<StatusBlock
				kind={scopesLoading ? 'loading' : 'empty'}
				size="lg"
				message={
					scopesLoading
						? 'Caricamento...'
						: 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
				}
			/>
		);
	}

	if (showOverview) {
		return (
			<ReportBuilderView
				{...toggle}
				cerNome=""
				members={[]}
				initialGenerated={[]}
				overviewGridSlot={scopes.map((leaf) => (
					<ReportConfigCardWiring
						key={leaf.orgId}
						leaf={leaf}
						onOpen={() => openConfig(leaf.orgId)}
					/>
				))}
			/>
		);
	}

	return <ReportsSingleWiring key={effectiveLeaf.orgId} leaf={effectiveLeaf} toggle={toggle} />;
}

/** Alimenta una card della «Panoramica»: la composizione soci di una configurazione sola. */
function ReportConfigCardWiring({ leaf, onOpen }: { leaf: ScopeLeaf; onOpen: () => void }) {
	const { config } = useScopedConfig(leaf.orgId);
	const { members, isLoading } = useMembers(leaf.orgId);

	if (isLoading) {
		return <ReportBuilderConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const attivi = members.filter((m) => m.stato !== 'withdrawn');

	const data: ReportBuilderConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		sociAttivi: attivi.length,
		produttori: attivi.filter((m) => PRODUCER_ROLES.has(m.ruolo)).length,
		// Il prosumer preleva anche: conta fra i produttori e fra i consumatori,
		// come nel dettaglio configurazione (INB-350).
		consumatori: attivi.filter((m) => m.ruolo === 'consumer' || m.ruolo === 'prosumer').length,
		inAttesa: members.filter((m) => m.stato === 'pending').length,
	};

	return <ReportBuilderConfigCard data={data} onClick={onOpen} />;
}

/**
 * Il generatore di report completo di una configurazione sola: puro cablaggio
 * dati. La `key` sull'orgId nel chiamante rimonta questo blocco quando si cambia
 * configurazione dal picker.
 *
 * La generazione è client-side: `report-export` costruisce il file (PDF via
 * jsPDF, Excel via SheetJS), `useGeneratedReports` conserva `spec` + riga in
 * `localStorage` così "apri"/"scarica" possono ricostruirlo dopo un refresh.
 */
function ReportsSingleWiring({ leaf, toggle }: { leaf: ScopeLeaf; toggle: ConfigToggleProps }) {
	const { config } = useScopedConfig(leaf.orgId);
	const { members, isLoading: membersLoading } = useMembers(leaf.orgId);
	const { plants } = usePlants(leaf.orgId);
	const plantOptions = useMemo(
		() =>
			plants.map((p) => ({
				id: p.id,
				label: `${p.nome} · ${p.potenzaKwp.toLocaleString('it-IT')} kWp`,
			})),
		[plants],
	);
	const { reports, add, remove, find } = useGeneratedReports(leaf.orgId);

	const generatedReports = useMemo(() => reports.map((r) => r.item), [reports]);

	// PDF mostrato nella finestra di anteprima dell'app (INB-381): l'URL `blob:`
	// si crea qui e si revoca alla chiusura, o se la pagina si smonta.
	const [preview, setPreview] = useState<{ url: string; title: string; spec: ReportSpec } | null>(
		null,
	);
	useEffect(() => () => {
		if (preview) URL.revokeObjectURL(preview.url);
	}, [preview]);

	/** PDF → finestra di anteprima; Excel → download (il browser non lo mostra). */
	function show(spec: ReportSpec) {
		if (spec.format === 'excel') {
			downloadReportBlob(spec);
			return;
		}
		setPreview({ url: reportPdfObjectUrl(spec), title: `${spec.title} - ${spec.periodLabel}`, spec });
	}

	/**
	 * I valori dei blocchi e il logo della CER, letti al momento della
	 * generazione (INB-380). Se la lettura fallisce il report si genera lo
	 * stesso, con i blocchi «dato non ancora disponibile»: mai numeri inventati.
	 */
	async function withReportData(spec: ReportSpec): Promise<ReportSpec> {
		try {
			const params: Record<string, string> = {
				orgId: leaf.orgId,
				scope: spec.scope,
				period: spec.period,
			};
			if (spec.memberId) params.memberId = spec.memberId;
			if (spec.plantId) params.plantId = spec.plantId;
			if (spec.customFrom && spec.customTo) {
				params.from = spec.customFrom;
				params.to = spec.customTo;
			}
			const data = await api.get<{
				values: Record<string, string | null>;
				cer: { nome: string; logoUrl: string | null } | null;
			}>('/api/reports/data', params);
			const logo = spec.flags.logo ? await loadReportLogo(data.cer?.logoUrl) : null;
			return {
				...spec,
				logo,
				sections: spec.sections.map((section) => ({
					...section,
					blocks: section.blocks.map((b) => ({ ...b, value: data.values[b.id] ?? null })),
				})),
			};
		} catch (err) {
			console.error('[reports] lettura dei valori fallita', err);
			toast.warning('Valori del report non disponibili: il file mostra solo la struttura.');
			return spec;
		}
	}

	async function handleGenerate(input: ReportSpec) {
		const spec = await withReportData(input);
		// Lo storico si salva senza il logo: un data URL per report riempirebbe
		// il localStorage. Riaprendo un report salvato, l'intestazione è «SLIMS».
		add({ ...spec, logo: null });
		try {
			show(spec);
			toast.success(
				spec.format === 'excel' ? 'Report Excel generato e scaricato' : 'Report PDF generato',
			);
		} catch (err) {
			console.error('[reports] generazione file fallita', err);
			toast.error(`Impossibile generare il file ${spec.format === 'excel' ? 'Excel' : 'PDF'}`);
		}
	}

	function handleOpen(id: string) {
		const entry = find(id);
		if (entry) show(entry.spec);
	}

	function handleDownload(id: string) {
		const entry = find(id);
		if (entry) downloadReportBlob(entry.spec);
	}

	function handleDelete(id: string) {
		if (!find(id)) return;
		remove(id);
		toast.success('Report eliminato');
	}

	return (
		<ReportBuilderView
			{...toggle}
			cerNome={config.nome || 'CER'}
			members={members}
			plants={plantOptions}
			aiEnabled={leaf.features?.aiSuggestions !== 'disabled'}
			initialGenerated={[]}
			membersLoading={membersLoading}
			generatedReports={generatedReports}
			onGenerateReport={handleGenerate}
			onOpenReport={handleOpen}
			onDownloadReport={handleDownload}
			onDeleteReport={handleDelete}
			pdfPreview={preview ? { url: preview.url, title: preview.title } : null}
			onClosePdfPreview={() => setPreview(null)}
			onDownloadPdfPreview={preview ? () => downloadReportBlob(preview.spec) : undefined}
		/>
	);
}
